import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as Ably from 'ably';
import { toast } from 'react-toastify';
import { whatsappApi } from '../api/whatsapp-api';
import { WhatsappContact, WhatsappMessage, CursorPaginationDTO } from '../types/whatsapp';
import newMessageSoundUrl from '../../../assets/sounds/new-message.mp3';
import chatAssignSoundUrl from '../../../assets/sounds/chat-assign.mp3';
import { ablyClient, getChannelName } from "../../../config/ably";

type Store = {
    messagesById: Record<string, WhatsappMessage>;
    messagesByContactId: Record<string, string[]>;
    contactsById: Record<string, WhatsappContact>;
    contactIds: string[];
};

export const useWhatsapp = () => {
    // Normalized Store
    const [store, setStore] = useState<Store>({
        messagesById: {},
        messagesByContactId: {},
        contactsById: {},
        contactIds: []
    });

    // Contacts UI State
    const [contactsLoading, setContactsLoading] = useState(true);
    const [contactsHasMore, setContactsHasMore] = useState(true);

    const [contactsCursorDate, setContactsCursorDate] = useState<string | undefined>(undefined);
    const [contactsCursorId, setContactsCursorId] = useState<string | undefined>(undefined);
    const [filterCurrentStateId, setFilterCurrentStateId] = useState<string | undefined>(undefined);

    // Selected Contact State
    const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

    // Messages UI State
    const [messagesPagination, setMessagesPagination] = useState<Record<string, {
        isLoading: boolean;
        hasMore: boolean;
        cursorDate?: string;
        cursorId?: string;
    }>>({});

    const messagesPaginationRef = useRef(messagesPagination);
    useEffect(() => {
        messagesPaginationRef.current = messagesPagination;
    }, [messagesPagination]);

    const currentPagination = selectedContactId ? messagesPagination[selectedContactId] : undefined;
    const messagesLoading = currentPagination?.isLoading ?? false;
    const messagesHasMore = currentPagination?.hasMore ?? true;

    // General UI State
    const [isSending, setIsSending] = useState(false);
    const [hasNewMessage, setHasNewMessage] = useState(false);
    const [unreadContacts, setUnreadContacts] = useState<Record<string, number>>({});
    const [audioStatus, setAudioStatus] = useState<'BLOCKED' | 'ALLOWED' | 'IGNORED'>('BLOCKED');
    const [showAudioUnlock, setShowAudioUnlock] = useState(false);

    const audioStatusRef = useRef(audioStatus);
    useEffect(() => { audioStatusRef.current = audioStatus; }, [audioStatus]);

    const playAudioEvent = useCallback((url: string) => {
        if (audioStatusRef.current === 'IGNORED') return;

        if (audioStatusRef.current === 'ALLOWED') {
            new Audio(url).play().catch(e => {
                console.warn('Audio play failed', e);
                if (e.name === 'NotAllowedError') {
                    setAudioStatus('BLOCKED');
                    setShowAudioUnlock(true);
                }
            });
        } else if (audioStatusRef.current === 'BLOCKED') {
            setShowAudioUnlock(true);
        }
    }, []);

    const activateAudio = useCallback(() => {
        setAudioStatus('ALLOWED');
        setShowAudioUnlock(false);
        const emptyAudio = new Audio("data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA");
        emptyAudio.play().catch(() => {});
    }, []);

    const ignoreAudio = useCallback(() => {
        setAudioStatus('IGNORED');
        setShowAudioUnlock(false);
    }, []);

    // Mutable Refs for Real-Time logic
    const selectedContactIdRef = useRef<string | null>(null);
    useEffect(() => {
        selectedContactIdRef.current = selectedContactId;
    }, [selectedContactId]);

    const storeRef = useRef<Store>(store);
    useEffect(() => {
        storeRef.current = store;
    }, [store]);

    // Derived States (Selectors / Memoization)
    const contacts = useMemo(() => {
        return store.contactIds.map(id => store.contactsById[id]).filter(Boolean);
    }, [store.contactIds, store.contactsById]);

    const messages = useMemo(() => {
        if (!selectedContactId) return [];
        const msgIds = store.messagesByContactId[selectedContactId] || [];
        const msgs = msgIds.map(id => store.messagesById[id]).filter(Boolean);
        return msgs.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }, [selectedContactId, store.messagesByContactId, store.messagesById]);

    const contactsRef = useRef<WhatsappContact[]>([]);
    useEffect(() => { contactsRef.current = contacts; }, [contacts]);


    const markContactAsRead = useCallback(async (contactId: string) => {
        try {
            await whatsappApi.markAsRead(contactId);
            setStore(prev => {
                const contact = prev.contactsById[contactId];
                if (contact && contact.lastMessage && contact.lastMessage.direction === 'inbound') {
                    return {
                        ...prev,
                        contactsById: {
                            ...prev.contactsById,
                            [contactId]: {
                                ...contact,
                                lastMessage: {
                                    ...contact.lastMessage,
                                    status: 'read'
                                }
                            }
                        }
                    };
                }
                return prev;
            });
        } catch (error) {
            console.error('Failed to mark as read', error);
        }
    }, []);

    function updateContactLastMessage(contact: any, message: any) {
        return {
            ...contact,
            lastMessage: message,
            lastMessageAt: message.createdAt || new Date(),
            lastMessageStatus: message.status
        };
    }

    // Helper: upsert contact & messages (Income message logic)
    const processIncomeMessage = useCallback((
        contactId: string,
        contactInfo: any,
        incomingMessage: WhatsappMessage,
        conversationInfo?: any
    ) => {
        setStore(prev => {
            let newContactsById = { ...prev.contactsById };
            let newContactIds = [...prev.contactIds];
            const existingContact = newContactsById[contactId];

            if (existingContact) {
                let updatedConversations = existingContact.conversations;
                if (conversationInfo) {
                    if (Array.isArray(existingContact.conversations)) {
                        const exists = existingContact.conversations.some((c: any) => c.id === conversationInfo.id);
                        if (exists) {
                            updatedConversations = existingContact.conversations.map((c: any) =>
                                c.id === conversationInfo.id ? { ...c, ...conversationInfo, closedAt: null } : c
                            );
                        } else {
                            updatedConversations = [{ ...conversationInfo, closedAt: null }, ...existingContact.conversations];
                        }
                    } else {
                        updatedConversations = [{ ...conversationInfo, closedAt: null }];
                    }
                }
                newContactsById[contactId] = {
                    ...existingContact,
                    lastMessage: incomingMessage,
                    lastMessageAt: incomingMessage.createdAt,
                    conversations: updatedConversations,
                    ...(contactInfo?.name && { name: contactInfo.name }),
                    ...(contactInfo?.waId && { waId: contactInfo.waId }),
                };
                newContactIds = [contactId, ...newContactIds.filter(id => id !== contactId)];
            } else {
                newContactsById[contactId] = {
                    id: contactId,
                    waId: contactInfo?.waId || '',
                    name: contactInfo?.name || contactInfo?.waId || 'Desconocido',
                    isVerified: false,
                    botActive: false,
                    lastMessage: incomingMessage,
                    lastMessageAt: incomingMessage.createdAt,
                    conversations: conversationInfo ? [{ ...conversationInfo, closedAt: null }] : []
                };
                newContactIds = [contactId, ...newContactIds];
            }

            let newMessagesById = { ...prev.messagesById };
            let contactMsgs = prev.messagesByContactId[contactId] || [];
            let newContactMsgs = [...contactMsgs];
            const msgKey = incomingMessage.id;

            let updatedMsgIds = false;
            let matchedTempId: string | null = null;
            if (incomingMessage.direction === 'outbound') {
                for (const tempKey of newContactMsgs) {
                    if (tempKey.startsWith('temp-')) {
                        const tmpMsg = newMessagesById[tempKey];
                        if (tmpMsg && tmpMsg.content === incomingMessage.content) {
                            matchedTempId = tempKey;
                            break;
                        }
                    }
                }
            }

            if (matchedTempId) {
                newMessagesById[msgKey] = { ...incomingMessage };
                delete newMessagesById[matchedTempId];
                const idx = newContactMsgs.indexOf(matchedTempId);
                if (idx > -1) newContactMsgs[idx] = msgKey;
                updatedMsgIds = true;
            } else {
                if (!newMessagesById[msgKey]) {
                    const isLoaded = messagesPaginationRef.current[contactId] !== undefined;
                    if (isLoaded || incomingMessage.direction === 'outbound') {
                        newContactMsgs.push(msgKey);
                        updatedMsgIds = true;
                    }
                }
                newMessagesById[msgKey] = incomingMessage;
            }

            return {
                messagesById: newMessagesById,
                messagesByContactId: updatedMsgIds
                    ? { ...prev.messagesByContactId, [contactId]: newContactMsgs }
                    : prev.messagesByContactId,
                contactsById: newContactsById,
                contactIds: newContactIds
            };
        });
    }, []);

    // Loaders
    const loadContacts = useCallback(async (cursorDate?: string, cursorId?: string, currentStateId?: string) => {
        if (!cursorDate || !cursorId) setContactsLoading(true);
        try {
            const params: CursorPaginationDTO = { limit: 15 }; //TODO: Paginación de contactos
            if (cursorDate && cursorId) {
                params.cursorDate = cursorDate;
                params.cursorId = cursorId;
            }
            if (currentStateId
            ) {
                params.currentStateId = currentStateId;
            }

            const response = await whatsappApi.getContacts(params);

            setStore(prev => {
                const newContactsById = { ...prev.contactsById };
                const newContactIds = (cursorDate && cursorId) ? [...prev.contactIds] : [];
                const seen = new Set(newContactIds);

                const newUnreadContacts: Record<string, number> = {};

                response.data.forEach((c: any) => {
                    newContactsById[c.id] = c;
                    if (!seen.has(c.id)) {
                        newContactIds.push(c.id);
                        seen.add(c.id);
                    }
                    if (c.unreadCount) {
                        newUnreadContacts[c.id] = c.unreadCount;
                    }
                });

                if (!cursorDate && !cursorId) {
                    setUnreadContacts(newUnreadContacts);
                } else {
                    setUnreadContacts(prev => ({ ...prev, ...newUnreadContacts }));
                }

                return { ...prev, contactsById: newContactsById, contactIds: newContactIds };
            });

            setContactsHasMore(response.hasMore);

            // Recibimos los dos cursores del backend
            setContactsCursorDate(response.nextCursorDate || undefined);
            setContactsCursorId(response.nextCursorId || undefined);
        } catch (error) {
            console.error('Failed to load contacts', error);
        } finally {
            setContactsLoading(false);
        }
    }, []);

    const loadMoreContacts = useCallback(() => {
        if (!contactsLoading && contactsHasMore) {
            loadContacts(contactsCursorDate, contactsCursorId, filterCurrentStateId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactsLoading, contactsHasMore, contactsCursorDate, contactsCursorId, filterCurrentStateId]);

    const loadMessages = useCallback(async (contactId: string, cursorDate?: string, cursorId?: string) => {
        if (!contactId) return;
        setMessagesPagination(prev => ({
            ...prev,
            [contactId]: { ...(prev[contactId] || { hasMore: true }), isLoading: true }
        }));
        try {
            const params: any = { limit: 20 };
            if (cursorDate && cursorId) {
                params.cursorDate = cursorDate;
                params.cursorId = cursorId;
            }

            const response = await whatsappApi.getMessages(contactId, params);
            const ascData = [...response.data].sort(
                (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            );

            setStore(prev => {
                const newMessagesById = { ...prev.messagesById };
                const contactIdsArr = prev.messagesByContactId[contactId] || [];
                const newIdsSet = new Set(contactIdsArr);
                const toAdd: string[] = [];

                ascData.forEach(m => {
                    const key = m.id;
                    newMessagesById[key] = m;
                    if (!newIdsSet.has(key)) {
                        toAdd.push(key);
                        newIdsSet.add(key);
                    }
                });

                return {
                    ...prev,
                    messagesById: newMessagesById,
                    messagesByContactId: {
                        ...prev.messagesByContactId,
                        [contactId]: cursorDate ? [...toAdd, ...contactIdsArr] : [...contactIdsArr, ...toAdd]
                    }
                };
            });

            setMessagesPagination(prev => ({
                ...prev,
                [contactId]: {
                    isLoading: false,
                    hasMore: response.hasMore,
                    cursorDate: response.nextCursorDate || undefined,
                    cursorId: response.nextCursorId || undefined
                }
            }));
        } catch (error) {
            console.error('Failed to load messages', error);
            setMessagesPagination(prev => ({
                ...prev,
                [contactId]: { ...(prev[contactId] || { hasMore: true }), isLoading: false }
            }));
        }
    }, []);

    const loadMoreMessages = useCallback(() => {
        if (!selectedContactId) return;
        const pagination = messagesPagination[selectedContactId] || { isLoading: false, hasMore: true };
        if (!pagination.isLoading && pagination.hasMore) {
            loadMessages(selectedContactId, pagination.cursorDate, pagination.cursorId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messagesPagination, selectedContactId, loadMessages]);

    // Setup Ably
    useEffect(() => {
        const channel = ablyClient.channels.get(
            getChannelName('whatsapp-updates')
        );

        channel.subscribe((msg: Ably.Message) => {
            const data = msg.data;
            if (!data) return;

            const eventType = msg.name;

            const contactId = data.contact_id || data.contactId || data.conversation?.contactId || data.conversation?.contact?.id;

            // 1. incomeMessage
            if (eventType === 'incomeMessage') {
                const incomingMessage: WhatsappMessage | undefined = data.message || data;
                const contactInfo = data.conversation?.contact || data.contact;

                if (!contactId || !incomingMessage?.id) return;

                processIncomeMessage(contactId, contactInfo, incomingMessage, data.conversation);

                if (selectedContactIdRef.current !== contactId) {
                    if (data.unreadCount !== undefined) {
                        setUnreadContacts(prev => ({
                            ...prev,
                            [contactId]: data.unreadCount
                        }));
                    } else if (incomingMessage.direction === 'inbound') {
                        setUnreadContacts(prev => ({
                            ...prev,
                            [contactId]: (prev[contactId] || 0) + 1
                        }));
                    }
                    playAudioEvent(newMessageSoundUrl);
                } else {
                    markContactAsRead(contactId);
                    playAudioEvent(newMessageSoundUrl);
                }
                return;
            }

            // 2. update_message
            if (eventType === 'update_message' || data.status) {
                const messageId = data.id;
                const status = data.status;

                const incomingMessage: WhatsappMessage | undefined = data.message || (data.content ? data : undefined);

                if (eventType === 'update_message' || (messageId && status && !incomingMessage?.content)) {
                    if (!contactId || !messageId) return;

                    setStore(prev => {
                        const existingMsg = prev.messagesById[messageId];
                        if (!existingMsg || existingMsg.status === status) return prev;

                        const newState = {
                            ...prev,
                            messagesById: {
                                ...prev.messagesById,
                                [messageId]: { ...existingMsg, status }
                            }
                        };

                        const contact = newState.contactsById[contactId];
                        if (contact && contact.lastMessage?.id === messageId && contact.lastMessage.status !== status) {
                            newState.contactsById = {
                                ...newState.contactsById,
                                [contactId]: {
                                    ...contact,
                                    lastMessage: { ...contact.lastMessage, status }
                                }
                            };
                        }
                        return newState;
                    });
                    return;
                }
            }

            // 2. bot_inactive
            if (eventType === 'bot_inactive') {
                playAudioEvent(chatAssignSoundUrl);
                loadContacts(undefined, undefined, filterCurrentStateId);
                if (contactId === selectedContactIdRef.current) {
                    loadMessages(contactId, undefined);
                }
                return;
            }
        });

        return () => {
            channel.unsubscribe();
        };
    }, [processIncomeMessage, loadContacts, loadMessages, playAudioEvent, markContactAsRead, filterCurrentStateId]);

    // Fetch initial contacts on mount
    useEffect(() => {
        loadContacts(undefined, undefined, filterCurrentStateId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCurrentStateId]);

    // Fetch messages when a contact is selected
    useEffect(() => {
        if (selectedContactId) {
            setUnreadContacts(prev => {
                if (prev[selectedContactId]) {
                    const newUnread = { ...prev };
                    delete newUnread[selectedContactId];
                    return newUnread;
                }
                return prev;
            });

            setHasNewMessage(false);

            // Mark as read in API
            markContactAsRead(selectedContactId);

            // Check cache
            if (!messagesPaginationRef.current[selectedContactId]) {
                loadMessages(selectedContactId, undefined, undefined);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedContactId, markContactAsRead]);

    // Actions
    const sendMessage = async (content: string, replyMessageId?: string, file?: File) => {
        if (!selectedContactId || (!content.trim() && !file)) return;

        let replyMessageData = null;
        if (replyMessageId) {
            const foundMsg = store.messagesById[replyMessageId];
            if (foundMsg) {
                replyMessageData = { id: foundMsg.id, content: foundMsg.content };
            }
        }

        let messageType = 'text';
        let messageContent: any = content;

        if (file) {
            if (file.type.startsWith('image/')) {
                messageType = 'image';
            } else if (file.type.startsWith('video/')) {
                messageType = 'video';
            } else if (file.type.startsWith('audio/')) {
                messageType = 'audio';
            } else {
                messageType = 'document';
            }

            const previewUrl = URL.createObjectURL(file);
            messageContent = {
                id: previewUrl,
                url: previewUrl,
                filename: file.name,
                caption: content,
                mime_type: file.type,
                mimeType: file.type,
                size: file.size
            };
        }

        const tempId = `temp-${Date.now()}`;
        const optimisticMsg: WhatsappMessage = {
            id: tempId,
            messageId: tempId,
            direction: 'outbound',
            type: messageType,
            content: messageContent,
            status: 'sent',
            createdAt: new Date().toISOString(),
            isPending: true,
            replyMessageId,
            replyMessage: replyMessageData as any,
        };

        setStore(prev => {
            const newMsgsById = { ...prev.messagesById, [tempId]: optimisticMsg };
            const arr = prev.messagesByContactId[selectedContactId] || [];
            const newMsgsByContactId = { ...prev.messagesByContactId, [selectedContactId]: [...arr, tempId] };

            const newContacts = { ...prev.contactsById };
            let newContactIds = [...prev.contactIds];
            if (newContacts[selectedContactId]) {
                newContacts[selectedContactId] = { ...newContacts[selectedContactId], lastMessage: optimisticMsg, lastMessageAt: optimisticMsg.createdAt };
                newContactIds = [selectedContactId, ...newContactIds.filter(id => id !== selectedContactId)];
            }

            return {
                messagesById: newMsgsById,
                messagesByContactId: newMsgsByContactId,
                contactsById: newContacts,
                contactIds: newContactIds
            };
        });

        setIsSending(true);
        try {
            const messageResponse = await whatsappApi.sendMessage(selectedContactId, content, replyMessageId, file);

            setStore(prev => {
                const tempMessage = prev.messagesById[tempId];

                if (!prev.messagesById[tempId]) return prev;

                const newId = messageResponse.id;

                const { [tempId]: _, ...rest } = prev.messagesById;

                const contactMessages =
                    prev.messagesByContactId[selectedContactId] || [];

                const contact = prev.contactsById[selectedContactId];
                if (!contact) return prev;

                return {
                    ...prev,
                    messagesById: {
                        ...rest,
                        [newId]: {
                            ...tempMessage,
                            ...messageResponse,
                            isPending: false,
                            isFailed: false
                        }
                    },
                    messagesByContactId: {
                        ...prev.messagesByContactId,
                        [selectedContactId]: contactMessages.map(id =>
                            id === tempId ? newId : id
                        )
                    },
                    contactsById: {
                        ...prev.contactsById,
                        [selectedContactId]: updateContactLastMessage(contact, messageResponse)
                    }
                };
            });
        } catch (error: any) {
            console.error('Failed to send message', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Error al enviar el archivo.';
            toast.error(errorMessage);
            setStore(prev => {
                if (!prev.messagesById[tempId]) return prev;
                return {
                    ...prev,
                    messagesById: {
                        ...prev.messagesById,
                        [tempId]: {
                            ...prev.messagesById[tempId],
                            isPending: false,
                            isFailed: true
                        }
                    }
                };
            });
        } finally {
            setIsSending(false);
        }
    };

    const resendMessage = async (oldMessageId: string, content: string) => {
        if (!selectedContactId) return;

        const oldMsg = store.messagesById[oldMessageId];

        if (!oldMsg) return;

        const replyMessageId = oldMsg?.replyMessageId;

        setStore(prev => {
            return {
                ...prev,
                messagesById: {
                    ...prev.messagesById,
                    [oldMessageId]: {
                        ...prev.messagesById[oldMessageId],
                        isPending: true,
                        isFailed: false,
                    }
                }
            };
        });

        try {
            const messageResponse = await whatsappApi.sendMessage(
                selectedContactId,
                content,
                replyMessageId
            );

            setStore(prev => {
                const oldMsg = prev.messagesById[oldMessageId];
                if (!oldMsg) return prev;

                const newId = messageResponse.id;

                const { [oldMessageId]: _, ...rest } = prev.messagesById;

                const contactMessages =
                    prev.messagesByContactId[selectedContactId] || [];

                const contact = prev.contactsById[selectedContactId];
                if (!contact) return prev;

                return {
                    ...prev,
                    messagesById: {
                        ...rest,
                        [newId]: {
                            ...oldMsg,
                            ...messageResponse,
                            isPending: false,
                            isFailed: false
                        }
                    },
                    messagesByContactId: {
                        ...prev.messagesByContactId,
                        [selectedContactId]: contactMessages.map(id =>
                            id === oldMessageId ? newId : id
                        )
                    },
                    contactsById: {
                        ...prev.contactsById,
                        [selectedContactId]: updateContactLastMessage(contact, messageResponse)
                    }
                };
            });
        } catch (error) {
            setStore(prev => ({
                ...prev,
                messagesById: {
                    ...prev.messagesById,
                    [oldMessageId]: {
                        ...prev.messagesById[oldMessageId],
                        isPending: false,
                        isFailed: true
                    }
                }
            }));
        }
    };

    const loadTargetMessage = async (targetMessageId: string): Promise<boolean> => {
        if (!selectedContactId) return false;
        if (storeRef.current.messagesById[targetMessageId]) return true;

        setMessagesPagination(prev => ({
            ...prev,
            [selectedContactId]: { ...(prev[selectedContactId] || { hasMore: true }), isLoading: true }
        }));
        
        let currentPagination = messagesPagination[selectedContactId] || { hasMore: true };
        let currentCursorDate = currentPagination.cursorDate;
        let currentCursorId = currentPagination.cursorId;
        let found = false;
        let hasMore = currentPagination.hasMore;

        try {
            while (!found && hasMore) {
                const params: any = { limit: 50 };
                if (currentCursorDate && currentCursorId) {
                    params.cursorDate = currentCursorDate;
                    params.cursorId = currentCursorId;
                }

                const response = await whatsappApi.getMessages(selectedContactId, params);
                const ascData = [...response.data].sort(
                    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                );

                const isTargetInChunk = ascData.some(m => m.id === targetMessageId);
                if (isTargetInChunk) {
                    found = true;
                }

                setStore(prev => {
                    const newMessagesById = { ...prev.messagesById };
                    const contactIdsArr = prev.messagesByContactId[selectedContactId] || [];
                    const newIdsSet = new Set(contactIdsArr);
                    const toAdd: string[] = [];

                    ascData.forEach(m => {
                        const key = m.id;
                        newMessagesById[key] = m;
                        if (!newIdsSet.has(key)) {
                            toAdd.push(key);
                            newIdsSet.add(key);
                        }
                    });

                    return {
                        ...prev,
                        messagesById: newMessagesById,
                        messagesByContactId: {
                            ...prev.messagesByContactId,
                            [selectedContactId]: [...toAdd, ...contactIdsArr]
                        }
                    };
                });

                const nextCursorDate = response.nextCursorDate || undefined;
                const nextCursorId = response.nextCursorId || undefined;
                const nextHasMore = response.hasMore;

                currentCursorDate = nextCursorDate;
                currentCursorId = nextCursorId;
                hasMore = nextHasMore;

                setMessagesPagination(prev => ({
                    ...prev,
                    [selectedContactId]: {
                        isLoading: true, // keep true while looping
                        hasMore: nextHasMore,
                        cursorDate: nextCursorDate,
                        cursorId: nextCursorId
                    }
                }));

                if (found) break;
            }
        } catch (e) {
            console.error('Failed to jump to target message:', e);
        } finally {
            setMessagesPagination(prev => ({
                ...prev,
                [selectedContactId]: {
                    ...(prev[selectedContactId] || { hasMore: true }),
                    isLoading: false
                }
            }));
        }

        return found;
    };

    const dismissNewMessage = useCallback(() => {
        setHasNewMessage(false);
        if (selectedContactId) {
            setUnreadContacts(prev => {
                if (prev[selectedContactId]) {
                    const newUnread = { ...prev };
                    delete newUnread[selectedContactId];
                    return newUnread;
                }
                return prev;
            });
            const contact = contactsRef.current.find(c => c.id === selectedContactId);
            if (contact && !contact.botActive) {
                markContactAsRead(selectedContactId);
            }
        }
    }, [selectedContactId, markContactAsRead]);

    const updateContactCustomerState = useCallback((contactId: string, newState: any) => {
        setStore(prev => {
            const contact = prev.contactsById[contactId];
            if (!contact || !contact.customer) return prev;

            return {
                ...prev,
                contactsById: {
                    ...prev.contactsById,
                    [contactId]: {
                        ...contact,
                        customer: {
                            ...contact.customer,
                            currentState: newState,
                            currentStateId: newState.id
                        }
                    }
                }
            };
        });
    }, []);

    const updateContactCustomerInfo = useCallback((contactId: string, data: any) => {
        setStore(prev => {
            const contact = prev.contactsById[contactId];
            if (!contact) return prev;

            const updatedCustomer = contact.customer 
                ? { ...contact.customer, ...data }
                : { id: data.id || contactId, ...data };

            return {
                ...prev,
                contactsById: {
                    ...prev.contactsById,
                    [contactId]: {
                        ...contact,
                        customer: updatedCustomer
                    }
                }
            };
        });
    }, []);

    const selectedContact = store.contactsById[selectedContactId || ''];

    return {
        contacts,
        contactsLoading,
        contactsHasMore,
        loadMoreContacts,
        loadContacts,
        unreadContacts,

        selectedContactId,
        setSelectedContactId,
        selectedContact,

        messages,
        messagesLoading,
        messagesHasMore,
        loadMoreMessages,
        loadTargetMessage,

        hasNewMessage,
        dismissNewMessage,

        sendMessage,
        resendMessage,
        isSending,

        audioStatus,
        showAudioUnlock,
        activateAudio,
        ignoreAudio,
        updateContactCustomerState,
        updateContactCustomerInfo,

        filterCurrentStateId,
        setFilterCurrentStateId,
    };
};
